import { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LogOut, FileText, DollarSign, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ClientDashboard() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientData();
  }, []);

  const fetchClientData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/clients`, { headers });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50" data-testid="client-dashboard">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Client Portal</h1>
              <p className="text-sm text-gray-600" style={{fontFamily: 'Inter, sans-serif'}}>Welcome, {user.full_name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <Card className="p-8 bg-gradient-to-br from-orange-500 to-amber-600 text-white">
          <h2 className="text-2xl font-bold mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Welcome to Your Dashboard</h2>
          <p style={{fontFamily: 'Inter, sans-serif'}}>View your service details, billing information, and maintenance reports.</p>
        </Card>

        <div>
          <h3 className="text-2xl font-semibold mb-6 text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Your Services</h3>
          
          {clients.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <p>No service records found. Please contact admin.</p>
            </Card>
          ) : (
            <div className="grid gap-6">
              {clients.map((client) => (
                <Card key={client.id} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{client.name}</h4>
                      <p className="text-sm text-gray-600">{client.email}</p>
                      <p className="text-sm text-gray-600">{client.phone}</p>
                    </div>
                    <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mt-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">AMC Value</p>
                      <p className="text-2xl font-bold text-blue-600">₹{client.amc_value.toLocaleString()}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Start Date</p>
                      <p className="text-lg font-semibold text-green-600">{client.amc_start_date}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">End Date</p>
                      <p className="text-lg font-semibold text-purple-600">{client.amc_end_date}</p>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <Button className="flex-1 bg-orange-600 hover:bg-orange-700">
                      <FileText className="h-4 w-4 mr-2" />
                      View Invoice
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Payment History
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}